import { useNavigate } from "react-router-dom";
import { useInternalRouter, useInternalRouterUtils } from "../stores/internalRouter";

interface UseGoBackCallbackProps {
  fallback?: string;
  searchLastRouteFromFallback?: boolean;
  searchLastRouteIgnoringFilters?: boolean;
  searchLastRouteFromReference?: string;
  removeFilters?: boolean;
};

/**
 *
 * @param searchLastRouteFromFallback
 * ex:
 * current route -> /users/123/actions
 * fallback -> "/users"
 * return (navigate to) => /users?search=123
 *
 *
 * @param searchLastRouteIgnoringFilters
 *  * ex:
 * current route -> /establishment-bills/456
 * previous route -> /establishments/123/bills?search=456
 * return (navigate to) =>  /establishments/123/bills?search=456
 *
 *  * ex:
 * current route -> /establishment-bills/456
 * previous route -> /establishment-bills?search=456
 * return (navigate to) => /establishment-bills?search=456
 *
 *  * ex:
 * current route -> /users/123/actions
 * previous route -> /users/123
 * return (navigate to) => /users/123
 *
 */

export const useGoBack = () => {
  const { paths, removeLastPath, removePathsFromIndex } = useInternalRouter();
  const {
    getRouteIndex,
    getRouteFirstIndex,
    getRouteFromIndex,
    getPreviousRoute,
    getPreviousRouteFromPathname,
    getPreviousRouteWithoutFilters,
    getPath,
  } = useInternalRouterUtils();

  const navigate = useNavigate();

  const thereIsAPrevPage = paths.length > 1;

  if (thereIsAPrevPage) {
    return ({
      fallback,
      searchLastRouteFromFallback,
      searchLastRouteIgnoringFilters,
      searchLastRouteFromReference,
      removeFilters
    }: UseGoBackCallbackProps) => {
      const previousRouteWithoutFilters = getPreviousRouteWithoutFilters();

      if (searchLastRouteIgnoringFilters && previousRouteWithoutFilters && previousRouteWithoutFilters?.pathname !== '/login') {
        removePathsFromIndex(getRouteIndex(previousRouteWithoutFilters) + 1);
        return navigate(getPath(previousRouteWithoutFilters, removeFilters));
      }

      if (searchLastRouteFromReference && previousRouteWithoutFilters && previousRouteWithoutFilters?.pathname !== '/login') {
        const routeFromReference = getPreviousRouteFromPathname(searchLastRouteFromReference);

        if (routeFromReference) {
          const previousRouteFromReference = getRouteFromIndex(getRouteFirstIndex(routeFromReference) + 1);

          if (previousRouteFromReference) {
            removePathsFromIndex(getRouteIndex(previousRouteWithoutFilters) + 1);
            return navigate(getPath(previousRouteFromReference, removeFilters));
          }
        }

        return navigate(fallback || '/');
      }

      if (searchLastRouteFromFallback && fallback) {
        const previousRouteFromFallback = getPreviousRouteFromPathname(fallback);

        if (previousRouteFromFallback) {
          removePathsFromIndex(getRouteIndex(previousRouteFromFallback) + 1);
          return navigate(getPath(previousRouteFromFallback, removeFilters));
        }

        return navigate(fallback || '/');
      } else {
        const previousRoute = getPreviousRoute();

        if (previousRoute && previousRoute?.pathname !== '/login') {
          navigate(getPath(previousRoute, removeFilters));
          removeLastPath();
        } else {
          navigate(fallback || '/');
        }
      }
    };
  }

  return ({ fallback }: UseGoBackCallbackProps) => navigate(fallback || '/');
};
